import Dexie, { type Table } from 'dexie';
import { strokeFromWire, strokeToWire, type Notebook, type Page, type Stroke, type StrokeWire, type SyncOp, type TextBlock } from 'shared';

export interface OutboxRow { seq?: number; op: SyncOp }
export interface MetaRow { key: string; value: unknown }
export interface ItemCacheRow { id: string; pageId: string; rows: number[]; kind: string; status: string; title: string; todoistState?: string | null }

class CadernoDB extends Dexie {
  notebooks!: Table<Notebook, string>;
  pages!: Table<Page, string>;
  strokes!: Table<StrokeWire, string>;
  outbox!: Table<OutboxRow, number>;
  meta!: Table<MetaRow, string>;
  items!: Table<ItemCacheRow, string>;
  textBlocks!: Table<TextBlock, string>;

  constructor() {
    super('caderno-vivo');
    this.version(1).stores({
      notebooks: 'id, sort, deletedAt',
      pages: 'id, notebookId, sort, pageDate, deletedAt',
      strokes: 'id, pageId, row, [pageId+row], deletedAt',
      outbox: '++seq',
      meta: 'key',
      items: 'id, pageId',
    });
    this.version(2).stores({
      textBlocks: 'id, pageId, row',
    });
  }
}

export const db = new CadernoDB();

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return (row?.value as T) ?? fallback;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export async function loadStrokes(pageId: string): Promise<Stroke[]> {
  const rows = await db.strokes.where('pageId').equals(pageId).toArray();
  return rows.filter((r) => !r.deletedAt).map(strokeFromWire);
}

/** Toda escrita é local primeiro e entra na fila; a rede é assunto de depois. */
export async function saveStroke(stroke: Stroke): Promise<void> {
  const wire = strokeToWire(stroke);
  await db.transaction('rw', db.strokes, db.outbox, async () => {
    await db.strokes.put(wire);
    await db.outbox.add({ op: { op: 'put', entity: 'stroke', id: wire.id, payload: wire, at: Date.now() } });
  });
}

export async function deleteStrokes(ids: string[]): Promise<void> {
  const at = Date.now();
  await db.transaction('rw', db.strokes, db.outbox, async () => {
    for (const id of ids) {
      await db.strokes.update(id, { deletedAt: at });
      await db.outbox.add({ op: { op: 'delete', entity: 'stroke', id, at } });
    }
  });
}

export async function savePage(page: Page): Promise<void> {
  await db.transaction('rw', db.pages, db.outbox, async () => {
    await db.pages.put(page);
    await db.outbox.add({ op: { op: 'put', entity: 'page', id: page.id, payload: page, at: page.updatedAt } });
  });
}

export async function saveNotebook(nb: Notebook): Promise<void> {
  await db.transaction('rw', db.notebooks, db.outbox, async () => {
    await db.notebooks.put(nb);
    await db.outbox.add({ op: { op: 'put', entity: 'notebook', id: nb.id, payload: nb, at: nb.updatedAt } });
  });
}

/**
 * Pede ao navegador para não descartar o IndexedDB quando faltar espaço
 * (SPEC §13.4). Sem isso o Chrome pode apagar meses de caderno em silêncio.
 */
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted()) return true;
  return navigator.storage.persist();
}

export async function storageEstimate(): Promise<{ usedMb: number; quotaMb: number } | null> {
  if (!navigator.storage?.estimate) return null;
  const e = await navigator.storage.estimate();
  return { usedMb: Math.round((e.usage ?? 0) / 1e6), quotaMb: Math.round((e.quota ?? 0) / 1e6) };
}

export async function saveTextBlock(block: TextBlock): Promise<void> {
  await db.transaction('rw', db.textBlocks, db.outbox, async () => {
    await db.textBlocks.put(block);
    await db.outbox.add({ op: { op: 'put', entity: 'textBlock', id: block.id, payload: block, at: block.updatedAt } });
  });
}

export async function deleteTextBlock(id: string): Promise<void> {
  const at = Date.now();
  await db.transaction('rw', db.textBlocks, db.outbox, async () => {
    await db.textBlocks.update(id, { deletedAt: at, updatedAt: at });
    await db.outbox.add({ op: { op: 'delete', entity: 'textBlock', id, at } });
  });
}

export async function loadTextBlocks(pageId: string): Promise<TextBlock[]> {
  const rows = await db.textBlocks.where('pageId').equals(pageId).toArray();
  return rows.filter((r) => !r.deletedAt);
}

/** Apagar vai para a lixeira (SPEC §8): nada some na hora. */
export async function trashNotebook(nb: Notebook): Promise<void> {
  const at = Date.now();
  await db.transaction('rw', db.notebooks, db.pages, db.outbox, async () => {
    await db.notebooks.update(nb.id, { deletedAt: at, updatedAt: at });
    // Espelha localmente a cascata que o servidor faz, para a biblioteca não
    // mostrar páginas órfãs enquanto o sync não acontece.
    const pages = await db.pages.where('notebookId').equals(nb.id).toArray();
    for (const p of pages) {
      if (!p.deletedAt) await db.pages.update(p.id, { deletedAt: at, updatedAt: at });
    }
    await db.outbox.add({ op: { op: 'delete', entity: 'notebook', id: nb.id, at } });
  });
}

export async function restoreNotebook(id: string): Promise<void> {
  const at = Date.now();
  const nb = await db.notebooks.get(id);
  if (!nb) return;
  const deletedAt = nb.deletedAt;
  await db.transaction('rw', db.notebooks, db.pages, db.outbox, async () => {
    await db.notebooks.update(id, { deletedAt: null, updatedAt: at });
    const pages = await db.pages.where('notebookId').equals(id).toArray();
    for (const p of pages) {
      if (p.deletedAt === deletedAt) await db.pages.update(p.id, { deletedAt: null, updatedAt: at });
    }
    await db.outbox.add({
      op: { op: 'put', entity: 'notebook', id, payload: { ...nb, deletedAt: null, updatedAt: at }, at },
    });
  });
}

export async function trashPage(page: Page): Promise<void> {
  const at = Date.now();
  await db.transaction('rw', db.pages, db.outbox, async () => {
    await db.pages.update(page.id, { deletedAt: at, updatedAt: at });
    await db.outbox.add({ op: { op: 'delete', entity: 'page', id: page.id, at } });
  });
}

export async function restorePage(id: string): Promise<void> {
  const at = Date.now();
  const page = await db.pages.get(id);
  if (!page) return;
  await db.transaction('rw', db.pages, db.outbox, async () => {
    await db.pages.update(id, { deletedAt: null, updatedAt: at });
    await db.outbox.add({
      op: { op: 'put', entity: 'page', id, payload: { ...page, deletedAt: null, updatedAt: at }, at },
    });
  });
}

/** Sumiu de vez no servidor: a cópia local tem de sair também, porque uma
 *  linha removida de verdade não deixa tombstone para o sync trazer. */
export async function forgetLocally(entity: 'notebook' | 'page', id: string): Promise<void> {
  if (entity === 'notebook') {
    await db.transaction('rw', db.notebooks, db.pages, db.strokes, db.textBlocks, async () => {
      const pages = await db.pages.where('notebookId').equals(id).toArray();
      for (const p of pages) {
        await db.strokes.where('pageId').equals(p.id).delete();
        await db.textBlocks.where('pageId').equals(p.id).delete();
      }
      await db.pages.where('notebookId').equals(id).delete();
      await db.notebooks.delete(id);
    });
    return;
  }
  await db.transaction('rw', db.pages, db.strokes, db.textBlocks, async () => {
    await db.strokes.where('pageId').equals(id).delete();
    await db.textBlocks.where('pageId').equals(id).delete();
    await db.pages.delete(id);
  });
}
